import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import { nanoid } from 'nanoid';
import _ from 'lodash';
import { Button, Select, TextInput, Checkbox, Radio } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import 'dayjs/locale/ko';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import {
  IApiResult,
  IOptionItem,
  ILeftMenu,
  ILoginUser,
  ISelect,
  ITableColumn,
  IPageContent,
  IComponentModal,
  IModal,
  ITablePaging,
} from '../interfaces/app.interface';
import { loginStore } from '../stores/login.store';
import { selectedPageStore } from '../stores/selectedPage.store';
import { modalStore, removeModalStore } from '../stores/modal.store';
import { PageLayout } from '../components/PageLayout';
import { PbFormGrid } from '../components/PbFormGrid';
import { PbFormGridCol } from '../components/PbFormGridCol';
import { pageContentStore } from '../stores/page.store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  addComponentModalStore,
  removeComponentModalStore,
} from '../stores/componentModal.store';
import {
  refreshListStore,
  removeRefreshListStore,
} from '../stores/refreshList.store';
import { MN2302220711 } from '../components/MN2302220711'; //등록,상세,수정 Component
import * as ntcbApi from '../apis/ntcb.api';
import * as cmnApi from '../apis/cmn.api';
import { PbAgGridReact } from '../components/PbAgGridReact';
import PbSelect from '../components/PbSelect/PbSelect.component';
import * as appUtil from '../utils/app.util';
import { decode } from 'html-entities';

/**
 * 게시판 > 메뉴얼
 * @constructor
 */
const MN2302220701Page = () => {
  // 언어를 정의함
  const { t } = useTranslation();
  // 로그인한 사용자 저장소를 정의함
  const [loginUser, setLoginUser] = useRecoilState<ILoginUser>(loginStore);
  // 선택한 페이지 저장소를 정의함
  const [selectedPage, setSelectedPage] =
    useRecoilState<ILeftMenu>(selectedPageStore);
  // 페이지 내용 저장소를 정의함
  const [pageContent, setPageContent] =
    useRecoilState<IPageContent>(pageContentStore);
  // 모달 저장소를 정의함
  const [modal, setModal] = useRecoilState<IModal>(modalStore);
  // 삭제할 모달 저장소를 정의함
  const [removeModal, setRemoveModal] = useRecoilState<any>(removeModalStore);
  // 추가할 컴포넌트 모달 저장소를 정의함
  const [addComponentModal, setAddComponentModal] =
    useRecoilState<IComponentModal | null>(addComponentModalStore);
  // 삭제할 컴포넌트 모달 저장소를 정의함
  const [removeComponentModal, setRemoveComponentModal] = useRecoilState<
    string | null
  >(removeComponentModalStore);
  // 목록 새로고침 저장소를 정의함
  const [refreshList, setRefreshList] =
    useRecoilState<string[]>(refreshListStore);
  // 삭제할 목록 새로고침 저장소를 정의함
  const [removeRefreshList, setRemoveRefreshList] = useRecoilState<string>(
    removeRefreshListStore,
  );

  /**************************** API 호출 ****************************/
  //메뉴얼 유형 공통코드 조회
  const getManualTypeCode = () => {
    cmnApi
      .getDetailCodesAll({
        grpCodeId: 'MNL_TYPE',
      })
      .then((data: IApiResult) => {
        if (data.data.list !== undefined) {
          let tmpOptionItem: IOptionItem[] = [];
          tmpOptionItem.push({
            label: '전체',
            value: '',
          });
          for (var code of data.data.list) {
            tmpOptionItem.push({
              label: code.dtlCodeDscr,
              value: code.dtlCodeId.toLowerCase(),
            });
          }
          // 검색 폼 그리드 > 고객사명 > 셀렉트의 아이템에 적용함
          setManualCategorySelect('');
          setManualCategorySelectItem(tmpOptionItem);
          // setManualCategorySelect({
          //   value: '',
          //   item: tmpOptionItem,
          // });
        }
      });
  };

  //메뉴얼 목록 조회
  const getManualList = () => {
    // 메뉴얼 테이블의 로딩 여부에 적용함
    setManualTableLoading(true);

    ntcbApi
      .getManuals({
        mnlTypeCode: manualCategorySelect,
        searchKeyword: manualTitleInput,
        pageSize: manualTablePaging.rowPerPage,
        currPageIdx: manualTablePagingCurrentPageRef.current,
      })
      .then((data: IApiResult) => {
        if (data.data.list !== undefined) {
          // 검색 결과 테이블에 적용함
          setManualTable((pre: { column: ITableColumn[]; data: any }) => ({
            ...pre,
            data: data.data.list,
          }));

          // 테이블을 페이징함
          setManualTablePaging((pre: ITablePaging) => ({
            ...pre,
            totalPage: data.data.page.totPageCnt,
            totalRow: data.data.page.totItemCnt,
          }));
        }

        // 메뉴얼 테이블의 로딩 여부에 적용함
        setManualTableLoading(false);
      });
  };

  /**************************** STATE ****************************/
  //제목 검색어
  const [manualTitleInput, setManualTitleInput] = useState<string>('');

  //메뉴얼 유형 셀렉트 state
  const [manualCategorySelect, setManualCategorySelect] = useState<string>('');
  const [manualCategorySelectItem, setManualCategorySelectItem] = useState<
    IOptionItem[]
  >([]);

  //메뉴얼 테이블
  const manualTableRef = useRef(null);
  //메뉴얼 테이블 state
  const [manualTable, setManualTable] = useState<{
    column: any[];
    data: any;
  }>({ column: [], data: [] });

  // 메뉴얼 테이블 페이징을 정의함
  const [manualTablePaging, setManualTablePaging] = useState<ITablePaging>({
    totalPage: 1,
    totalRow: 0,
    rowPerPage: 10,
    currentPage: 1,
  });
  const manualTablePagingCurrentPageRef = useRef<number>(1);

  // 메뉴얼 테이블의 로딩 여부를 정의함
  const [manualTableLoading, setManualTableLoading] = useState<boolean>(false);

  // 검색 폼 그리드 > 상세검색의 나타냄을 정의함
  const [hideDetailSearch, setHideDetailSearch] = useState<boolean>(false);

  /**************************** ON EVENT LISTENER ****************************/
  //메뉴얼 제목 검색어 이벤트
  const handleManualTitleInput_onChange = (event: any) => {
    setManualTitleInput(event.target.value);
  };
  //메뉴얼 유형 셀렉트 변경 이벤트
  const handleManualCategorySelect_onChange = (event: any) => {
    setManualCategorySelect(event);
    // setManualCategorySelect({
    //   ...manualCategorySelect,
    //   value: event,
    // });
  };
  //검색 조건 초기화 버튼 이벤트
  const handleCancelBtn_onClick = () => {
    setManualTitleInput('');
    setManualCategorySelect('');
    // setManualCategorySelect((pre: ISelect) => ({
    //   ...pre,
    //   value: '',
    // }));
  };
  //제목 검색 on keyup
  const handleManualTitleInput__onEnterKeyPress = (event: any) => {
    if (event.keyCode === 13) {
      // 테이블의 페이지를 변경함
      manualTablePagingCurrentPageRef.current = 1;
      setManualTablePaging((pre: ITablePaging) => ({
        ...pre,
        currentPage: manualTablePagingCurrentPageRef.current,
      }));

      getManualList();
    }
  };
  //제목 검색 버튼 이벤트
  const handleManualSearchBtn_onClick = () => {
    // 테이블의 페이지를 변경함
    manualTablePagingCurrentPageRef.current = 1;
    setManualTablePaging((pre: ITablePaging) => ({
      ...pre,
      currentPage: manualTablePagingCurrentPageRef.current,
    }));

    getManualList();
  };
  //메뉴얼 목록 클릭 (ROW Click)
  const handleManualTable_onRowDoubleClicked = (row: any) => {
    let tmpId: string = 'MN2302220711';
    //컴포넌트 모달을 추가함
    setAddComponentModal({
      id: tmpId,
      title: '메뉴얼 상세',
      content: (
        <MN2302220711 id={tmpId} brdId={row.data.brdId} useModifyMode={false} />
      ),
      size: 1500,
    });
  };
  //등록 버튼 클릭
  const handleManualAddBtn_onClick = () => {
    let tmpId: string = 'MN2302220711';
    setAddComponentModal({
      id: tmpId,
      title: '메뉴얼 등록',
      content: <MN2302220711 id={tmpId} />,
      size: 1500,
    });
  };

  // 검색 폼 그리드 > 검색어 > 상세검색 열기닫기 버튼을 클릭함
  const handleShowSearchDetailButton_onChange = () => {
    setHideDetailSearch(!hideDetailSearch);
  };

  /**************************** INIT ****************************/
  //메뉴얼 유형 셀렉트 초기화
  const initManualCategorySelect = () => {
    let tmpOptionItem: IOptionItem[] = [];

    tmpOptionItem.push(
      {
        label: '전체',
        value: '',
      },
      {
        label: '네트워크',
        value: 'NTWR',
      },
      {
        label: 'ESL(S/W)',
        value: 'ESL_SW',
      },
      {
        label: 'ESL(H/W)',
        value: 'ESL_HW',
      },
      {
        label: '기타',
        value: 'ETC',
      },
    );

    // 검색 폼 그리드 > 고객사명 > 셀렉트의 아이템에 적용함
    setManualCategorySelect('');
    setManualCategorySelectItem(tmpOptionItem);
    // setManualCategorySelect({
    //   value: '',
    //   item: tmpOptionItem,
    // });
  };

  //메뉴얼 테이블 초기화
  const initManualTable = () => {
    let tmpColumn: any[] = [];
    tmpColumn.push(
      {
        field: 'no',
        headerName: '번호',
        width: 80,
        suppressSizeToFit: true,
        type: 'rightAligned',
        valueFormatter: (params: any) =>
          appUtil.getFormatNumber(params.value || 0),
      },
      {
        field: 'mnlTypeName',
        headerName: '유형',
        width: 150,
        suppressSizeToFit: true,
      },
      {
        field: 'ttl',
        headerName: '제목',
        valueFormatter: (params: any) => decode(params.value),
      },
      {
        field: 'rgstName',
        headerName: '등록자',
        width: 100,
        suppressSizeToFit: true,
      },
      {
        field: 'rgstDate',
        headerName: '등록일',
        width: 130,
        suppressSizeToFit: true,
      },
      // {
      //   field: 'mdfcDate',
      //   headerName: '수정일',
      // },
    );
    setManualTable((pre: { column: any[]; data: any }) => ({
      ...pre,
      column: tmpColumn,
    }));
  };

  /**************************** USE EFFECT ****************************/
  // 페이지 로딩 후 한번만 실행함
  useEffect(() => {
    getManualTypeCode(); //메뉴얼 유형코드 셀렉트 초기화
    initManualTable();
    return () => {};
  }, []);

  //메뉴얼 테이블 값 초기화
  useEffect(() => {
    if (manualTable.column.length === 0) {
      return;
    }
    getManualList();
    return () => {};
  }, [manualTable.column]);

  //셀렉트 박스 변경 시
  // useEffect(() => {
  //   getManualList();
  //   return () => {};
  // }, [manualCategorySelect.value]);

  //목록 새로고침 시
  useEffect(() => {
    if (refreshList.length === 0) {
      return;
    }
    if (_.indexOf(refreshList, 'MN2302220701Table') > -1) {
      // 테이블의 페이지를 변경함
      manualTablePagingCurrentPageRef.current = 1;
      setManualTablePaging((pre: ITablePaging) => ({
        ...pre,
        currentPage: manualTablePagingCurrentPageRef.current,
      }));

      // 검색 결과 테이블 데이터를 불러옴
      getManualList();

      // 목록 새로고침 목록에서 제거함
      setRemoveRefreshList('MN2302220701Table');
    }
    return () => {};
  }, [refreshList]);

  return (
    <PageLayout
      // pageInfoBarLeftArea={<></>}
      pageInfoBarCenterArea={<></>}
      pageInfoBarRightArea={
        <>
          <Button
            color="indigo"
            radius="xl"
            onClick={handleManualAddBtn_onClick}
          >
            등록
          </Button>
        </>
      }
      enablePageInfoBarBackgroundColor={true}
    >
      {/* 페이지 내용 */}
      <div className="">
        {/* 검색 */}
        <div className="space-y-5">
          {/* 검색 폼 그리드 */}
          <PbFormGrid label="" cols={2}>
            <PbFormGridCol label="제목" colSpan={2}>
              <TextInput
                placeholder="검색어를 입력하세요."
                onChange={handleManualTitleInput_onChange}
                onKeyUp={handleManualTitleInput__onEnterKeyPress}
                value={manualTitleInput}
                className="w-full"
              />
              {/* 버튼 */}
              <div className="flex justify-center items-center space-x-2">
                <Button
                  color="indigo"
                  radius="xl"
                  onClick={handleManualSearchBtn_onClick}
                >
                  검색
                </Button>
                <Button
                  variant="outline"
                  color="gray"
                  radius="xl"
                  onClick={handleCancelBtn_onClick}
                >
                  검색 조건 초기화
                </Button>
                {/* 버튼 */}
                <Button
                  variant="outline"
                  color="gray"
                  radius="xl"
                  onClick={handleShowSearchDetailButton_onChange}
                >
                  {hideDetailSearch ? (
                    <>
                      상세검색 열기
                      <div className="ml-1 flex justify-center items-center">
                        <FontAwesomeIcon
                          icon={['fas', 'caret-down']}
                          className="w-5 h-5"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      상세검색 닫기
                      <div className="ml-1 flex justify-center items-center">
                        <FontAwesomeIcon
                          icon={['fas', 'caret-up']}
                          className="w-5 h-5"
                        />
                      </div>
                    </>
                  )}
                </Button>
              </div>
            </PbFormGridCol>

            <PbFormGridCol label="유형" colSpan={2} isHidden={hideDetailSearch}>
              <Select
                onChange={handleManualCategorySelect_onChange}
                data={manualCategorySelectItem}
                value={manualCategorySelect}
                className="w-full"
              />
            </PbFormGridCol>
          </PbFormGrid>

          {/* 테이블 */}
          <div className="space-y-3">
            <div className="flex justify-between items-center space-x-5">
              <div></div>

              <div className="flex justify-center items-center">
                <span className="text-base text-gray-600 font-bold">
                  총 {manualTablePaging.totalRow}건
                </span>
              </div>
            </div>

            {/* 테이블 */}
            <div className="w-full h-160">
              <PbAgGridReact
                columnDefs={manualTable.column}
                rowData={manualTable.data}
                loading={manualTableLoading}
                setLoading={setManualTableLoading}
                onRowDoubleClicked={handleManualTable_onRowDoubleClicked}
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                  wrapHeaderText: false,
                  autoHeaderHeight: true,
                }}
                rowSelection="single"
                sizeColumnsToFit={true}
                visiblePaging={true}
                paging={{
                  totalPage: manualTablePaging.totalPage,
                  currentPage: manualTablePaging.currentPage,
                }}
                onChangePage={(event: any) => {
                  // 테이블의 페이지를 변경함
                  manualTablePagingCurrentPageRef.current = event;
                  setManualTablePaging((pre: ITablePaging) => ({
                    ...pre,
                    currentPage: manualTablePagingCurrentPageRef.current,
                  }));

                  // 테이블 데이터를 불러옴
                  getManualList();
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default MN2302220701Page;
